/**
 * Ring Size — Email Verification
 *
 * Standalone web component for the no-login ring-size journey. Five-step state
 * machine: email -> code -> order -> size -> success. Talks to the Cloudflare
 * Worker (reebok-awaiting-order-worker). The session token lives in
 * localStorage for 60 minutes and is sent as `Authorization: Bearer`.
 *
 * Conventions mirror assets/variant-picker-reebok.js: constructor sets up
 * selectors, connectedCallback queries the DOM and binds, _privateMethods do
 * the work, disconnectedCallback unbinds. Raw ES6, loaded with defer.
 */

class RingSizeVerify extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      step: '[data-step]',
      email: '#rsv-email',
      code: '#rsv-code',
      codeGroup: '[data-rsv-code-group]',
      codeCell: '.ring-size-verify__code-cell',
      orderSelect: '#rsv-order',
      items: '[data-rsv-items]',
      message: '[data-rsv-message]',
      newOrder: '[data-rsv-neworder]',
      config: '[data-rsv-config]',
      submitBtn: '[data-action="submit"]',
      resendBtn: '[data-action="resend"]',
    };

    this.storageKey = 'rsv:session';
    this.resendCooldown = 60; // seconds, matches the Worker

    this.state = {
      email: '',
      mode: 'standard', // set from the section's data-mode in connectedCallback
      token: null,
      orders: [],
      currentOrder: null,
      items: [],
      selectedVariants: {},
    };

    this._cooldownTimer = null;
    this._confirming = false;
    this._handleClick = this._handleClick.bind(this);
    this._handleChange = this._handleChange.bind(this);
    this._handleKeydown = this._handleKeydown.bind(this);
    this._handleInput = this._handleInput.bind(this);
    this._handlePaste = this._handlePaste.bind(this);
  }

  connectedCallback() {
    this.api = (this.dataset.api || '').replace(/\/+$/, '');
    // Mode comes from the section setting (data-mode), not the URL.
    this.state.mode = this.dataset.mode === 'ellie' ? 'ellie' : 'standard';
    this.config = this._readConfig();

    this.refs = {
      steps: Array.from(this.querySelectorAll(this.selectors.step)),
      email: this.querySelector(this.selectors.email),
      code: this.querySelector(this.selectors.code),
      codeCells: Array.from(this.querySelectorAll(this.selectors.codeCell)),
      orderSelect: this.querySelector(this.selectors.orderSelect),
      items: this.querySelector(this.selectors.items),
      message: this.querySelector(this.selectors.message),
      newOrder: this.querySelector(this.selectors.newOrder),
      submitBtn: this.querySelector(this.selectors.submitBtn),
      resendBtn: this.querySelector(this.selectors.resendBtn),
    };

    this.addEventListener('click', this._handleClick);
    this.addEventListener('change', this._handleChange);
    this.addEventListener('keydown', this._handleKeydown);
    this.addEventListener('input', this._handleInput);
    this.addEventListener('paste', this._handlePaste);

    this._restoreSession();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick);
    this.removeEventListener('change', this._handleChange);
    this.removeEventListener('keydown', this._handleKeydown);
    this.removeEventListener('input', this._handleInput);
    this.removeEventListener('paste', this._handlePaste);
    if (this._cooldownTimer) clearInterval(this._cooldownTimer);
  }

  // ---------- config / session ----------

  _readConfig() {
    const fallback = {
      resendLabel: 'Resend code',
      resendCooldownLabel: 'Resend code in {seconds}s',
      orderPlaceholder: 'Select an order...',
      noOrders: 'No orders available for ring sizing.',
      sizePlaceholder: 'Select a size...',
      submitting: 'Submitting...',
      emailInvalid: 'Please enter a valid email address.',
      errors: {},
    };
    const el = this.querySelector(this.selectors.config);
    if (!el) return fallback;
    try {
      return { ...fallback, ...JSON.parse(el.textContent) };
    } catch {
      return fallback;
    }
  }

  _saveSession(token, mode, expiresIn) {
    this.state.token = token;
    this.state.mode = mode || this.state.mode;
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify({
          token,
          mode: this.state.mode,
          email: this.state.email,
          expiresAt: Date.now() + (expiresIn || 3600) * 1000,
        })
      );
    } catch {
      /* localStorage unavailable — token stays in memory for this page */
    }
  }

  _restoreSession() {
    const pageMode = this.state.mode; // derived from the URL in the constructor
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
    } catch {
      saved = null;
    }
    const valid = saved && saved.token && saved.expiresAt > Date.now();
    // Do not carry a session across journeys: a token verified on the standard
    // page must not be reused on the Ellie page (or vice versa).
    const sameMode = !saved || !saved.mode || saved.mode === pageMode;

    if (valid && sameMode) {
      this.state.token = saved.token;
      this.state.mode = saved.mode || pageMode;
      this.state.email = saved.email || '';
      this._loadOrders();
    } else {
      this._clearSession();
      this._show('email');
    }
  }

  _clearSession() {
    this.state.token = null;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* ignore */
    }
  }

  // ---------- UI helpers ----------

  _show(step) {
    this.refs.steps.forEach((el) => {
      const active = el.dataset.step === step;
      el.hidden = !active;
      el.classList.toggle('ring-size-verify__step--active', active);
    });
    this._clearMessage();
    const first = this.querySelector(`[data-step="${step}"] input, [data-step="${step}"] select`);
    if (first) first.focus({ preventScroll: true });
  }

  /**
   * Reveal or hide the size sub-step while keeping the order select above it
   * visible, so the customer can switch between orders at any time.
   */
  _setSizeVisible(visible) {
    const sizeStep = this.querySelector('[data-step="size"]');
    if (!sizeStep) return;
    sizeStep.hidden = !visible;
    sizeStep.classList.toggle('ring-size-verify__step--active', visible);
  }

  _message(text, type) {
    if (!text) return this._clearMessage();
    const el = this.refs.message;
    el.textContent = text;
    el.classList.remove('ring-size-verify__message--error', 'ring-size-verify__message--success');
    el.classList.add(`ring-size-verify__message--${type || 'error'}`);
    el.hidden = false;
  }

  _clearMessage() {
    this.refs.message.hidden = true;
    this.refs.message.textContent = '';
  }

  _setBusy(button, busy) {
    if (!button) return;
    button.classList.toggle('is-busy', busy);
    button.disabled = busy;
  }

  _err(key) {
    return (this.config.errors && this.config.errors[key]) || this.config.errors.generic || 'Something went wrong.';
  }

  // ---------- API ----------

  async _api(path, options = {}) {
    if (!this.api) return { ok: false, status: 0, data: null };
    const headers = { ...(options.headers || {}) };
    if (options.body) headers['Content-Type'] = 'application/json';
    if (this.state.token) headers.Authorization = `Bearer ${this.state.token}`;

    try {
      const res = await fetch(`${this.api}${path}`, {
        method: options.method || 'GET',
        headers,
        body: options.body ? JSON.stringify(options.body) : undefined,
      });
      const data = await res.json().catch(() => null);
      if (res.status === 401) {
        this._clearSession();
        this._show('email');
        this._message(this._err('expired'), 'error');
        return { ok: false, status: 401, data };
      }
      return { ok: res.ok, status: res.status, data };
    } catch {
      return { ok: false, status: 0, data: null, network: true };
    }
  }

  // ---------- events ----------

  _handleClick(event) {
    const trigger = event.target.closest('[data-action]');
    if (!trigger) return;
    const action = trigger.dataset.action;
    if (action === 'request') this._onRequest(trigger);
    else if (action === 'confirm') this._onConfirm(trigger);
    else if (action === 'resend') this._onResend(trigger);
    else if (action === 'submit') this._onSubmit(trigger);
  }

  _handleChange(event) {
    if (event.target.matches(this.selectors.orderSelect)) {
      this._onOrderSelect(event.target.value);
    } else if (event.target.matches('[data-rsv-size]')) {
      this._onSizeChange(event.target);
    }
  }

  _handleKeydown(event) {
    const target = event.target;

    if (target.matches(this.selectors.email)) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this._onRequest(this.querySelector('[data-action="request"]'));
      }
      return;
    }

    if (!target.matches(this.selectors.codeCell)) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      this._onConfirm(this.querySelector('[data-action="confirm"]'));
    } else if (event.key === 'Backspace' && !target.value) {
      // Empty cell: step back, clear the previous digit and focus it.
      const prev = this._adjacentCell(target, -1);
      if (prev) {
        event.preventDefault();
        prev.value = '';
        prev.focus();
      }
    } else if (event.key === 'ArrowLeft') {
      const prev = this._adjacentCell(target, -1);
      if (prev) {
        event.preventDefault();
        prev.focus();
      }
    } else if (event.key === 'ArrowRight') {
      const next = this._adjacentCell(target, 1);
      if (next) {
        event.preventDefault();
        next.focus();
      }
    }
  }

  _handleInput(event) {
    const cell = event.target;
    if (cell.matches(this.selectors.email)) {
      // Clear the custom validity message as soon as the user edits the field.
      cell.setCustomValidity('');
      cell.classList.remove('ring-size-verify__input--invalid');
      return;
    }
    if (!cell.matches(this.selectors.codeCell)) return;
    // Keep a single digit per cell; advance on entry.
    const digit = (cell.value || '').replace(/\D/g, '').slice(-1);
    cell.value = digit;
    this._clearCodeInvalid();
    if (digit) {
      const next = this._adjacentCell(cell, 1);
      if (next) next.focus();
    }
    this._maybeAutoConfirm();
  }

  _handlePaste(event) {
    const cell = event.target;
    if (!cell.matches(this.selectors.codeCell)) return;
    const text = (event.clipboardData && event.clipboardData.getData('text')) || '';
    const digits = text.replace(/\D/g, '').slice(0, this.refs.codeCells.length);
    if (!digits) return;
    event.preventDefault();
    this.refs.codeCells.forEach((c, i) => {
      c.value = digits[i] || '';
    });
    this._clearCodeInvalid();
    const focusIndex = Math.min(digits.length, this.refs.codeCells.length - 1);
    const focusCell = this.refs.codeCells[focusIndex];
    if (focusCell) focusCell.focus();
    this._maybeAutoConfirm();
  }

  /** Sibling code cell at `offset` (+1 / -1), or null at the ends. */
  _adjacentCell(cell, offset) {
    const i = this.refs.codeCells.indexOf(cell);
    if (i < 0) return null;
    return this.refs.codeCells[i + offset] || null;
  }

  _codeValue() {
    return this.refs.codeCells.map((c) => (c.value || '').replace(/\D/g, '')).join('');
  }

  _resetCode() {
    this.refs.codeCells.forEach((c) => {
      c.value = '';
    });
    this._clearCodeInvalid();
  }

  _setCodeInvalid() {
    this.refs.codeCells.forEach((c) => c.classList.add('ring-size-verify__code-cell--invalid'));
  }

  _clearCodeInvalid() {
    this.refs.codeCells.forEach((c) => c.classList.remove('ring-size-verify__code-cell--invalid'));
  }

  /** Submit automatically once all six digits are present. */
  _maybeAutoConfirm() {
    if (this._codeValue().length === this.refs.codeCells.length) {
      this._onConfirm(this.querySelector('[data-action="confirm"]'));
    }
  }

  // ---------- step 1: email ----------

  async _onRequest(button) {
    const input = this.refs.email;
    const email = (input.value || '').trim().toLowerCase();
    input.value = email;
    // Native HTML validation: `required` + type="email" give the browser's own
    // (localized) prompt. A stricter dotted-domain check, matching the backend,
    // is surfaced through the same native UI via setCustomValidity.
    input.setCustomValidity('');
    if (input.validity.valid && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      input.setCustomValidity(this.config.emailInvalid || 'Please enter a valid email address.');
    }
    if (!input.reportValidity()) {
      input.classList.add('ring-size-verify__input--invalid');
      return;
    }
    input.classList.remove('ring-size-verify__input--invalid');
    this.state.email = email;

    this._setBusy(button, true);
    const { ok, network } = await this._api('/verify/request', {
      method: 'POST',
      body: { email, mode: this.state.mode },
    });
    this._setBusy(button, false);

    if (network) return this._message(this._err('network'), 'error');
    if (!ok) return this._message(this._err('generic'), 'error');

    this._show('code');
    this._startResendCooldown();
  }

  // ---------- step 2: code ----------

  async _onConfirm(button) {
    if (this._confirming) return; // guard against auto-confirm + click racing
    const code = this._codeValue();
    if (!/^\d{6}$/.test(code)) {
      this._setCodeInvalid();
      this._message(this._err('invalid_code'), 'error');
      return;
    }
    this._clearCodeInvalid();

    this._confirming = true;
    this._setBusy(button, true);
    let result;
    try {
      result = await this._api('/verify/confirm', {
        method: 'POST',
        body: { email: this.state.email, code },
      });
    } finally {
      this._setBusy(button, false);
      this._confirming = false;
    }
    const { ok, data, network } = result;

    if (network) return this._message(this._err('network'), 'error');

    if (ok && data && data.token) {
      this._saveSession(data.token, data.mode, data.expiresIn);
      this._resetCode();
      await this._loadOrders();
      return;
    }

    const stateKey = data && data.state ? data.state : 'generic';
    this._setCodeInvalid();
    this._message(this._err(stateKey), 'error');
    if (this.refs.codeCells[0]) this.refs.codeCells[0].focus();
  }

  async _onResend(button) {
    if (button.disabled) return;
    this._setBusy(button, true);
    const { ok, network } = await this._api('/verify/request', {
      method: 'POST',
      body: { email: this.state.email, mode: this.state.mode },
    });
    this._setBusy(button, false);
    if (network) return this._message(this._err('network'), 'error');
    if (!ok) return this._message(this._err('generic'), 'error');
    this._clearMessage();
    this._resetCode();
    if (this.refs.codeCells[0]) this.refs.codeCells[0].focus();
    this._startResendCooldown();
  }

  _startResendCooldown() {
    const btn = this.refs.resendBtn;
    if (!btn) return;
    if (this._cooldownTimer) clearInterval(this._cooldownTimer);

    let remaining = this.resendCooldown;
    const label = this.config.resendCooldownLabel || 'Resend code in {seconds}s';
    const tick = () => {
      if (remaining <= 0) {
        clearInterval(this._cooldownTimer);
        this._cooldownTimer = null;
        btn.disabled = false;
        btn.textContent = this.config.resendLabel || 'Resend code';
        return;
      }
      btn.disabled = true;
      btn.textContent = label.replace('{seconds}', remaining);
      remaining -= 1;
    };
    tick();
    this._cooldownTimer = setInterval(tick, 1000);
  }

  // ---------- step 3: order ----------

  async _loadOrders() {
    const { ok, data, network } = await this._api('/orders');
    if (network) {
      this._show('email');
      return this._message(this._err('network'), 'error');
    }
    if (!ok || !data || !data.success) {
      this._show('order');
      return this._message(this._err('generic'), 'error');
    }

    const orders = data.data.orders || [];
    this.state.orders = orders;

    if (orders.length === 0) {
      this._show('order');
      this._populateOrderSelect([]);
      return this._message(this._err('no_order') || this.config.noOrders, 'error');
    }

    this._populateOrderSelect(orders);
    this._show('order');

    if (orders.length === 1) {
      this.refs.orderSelect.value = orders[0].name;
      await this._onOrderSelect(orders[0].name);
    }
  }

  _populateOrderSelect(orders) {
    const select = this.refs.orderSelect;
    if (orders.length === 0) {
      select.innerHTML = `<option value="">${this.config.noOrders}</option>`;
      return;
    }
    const opts = [`<option value="">${this.config.orderPlaceholder}</option>`];
    orders.forEach((order) => {
      const date = order.created_at ? new Date(order.created_at).toLocaleDateString() : '';
      const price = this._formatPrice(order.total_price, order.currency);
      const label = [order.name, date, price].filter(Boolean).join(' - ');
      opts.push(`<option value="${order.name}">${label}</option>`);
    });
    select.innerHTML = opts.join('');
  }

  async _onOrderSelect(orderName) {
    if (!orderName) {
      // Placeholder chosen: keep the order select visible, hide the size block.
      this.state.currentOrder = null;
      this.state.selectedVariants = {};
      this._setSizeVisible(false);
      this._clearMessage();
      return;
    }
    this.state.currentOrder = orderName;
    await this._loadItems(orderName);
  }

  // ---------- step 4: size ----------

  async _loadItems(orderName) {
    this._clearMessage();
    const { ok, data, network } = await this._api(`/orders/${encodeURIComponent(orderName)}/items`);
    if (network) {
      this._setSizeVisible(false);
      return this._message(this._err('network'), 'error');
    }
    if (!ok || !data || !data.success) {
      this._setSizeVisible(false);
      if (data && data.error === 'Order not found') {
        return this._message(this._err('no_order'), 'error');
      }
      return this._message(this._err('generic'), 'error');
    }

    const items = data.data.awaitingSizeItems || [];
    this.state.items = items;
    this.state.selectedVariants = {};

    if (items.length === 0) {
      this._setSizeVisible(false);
      return this._message(this._err('no_order') || this.config.noOrders, 'error');
    }

    await this._renderItems(items);
    this._setSizeVisible(true);
  }

  async _renderItems(items) {
    const blocks = [];
    const selectedOrder = this.state.orders.find((o) => o.name === this.state.currentOrder);
    const orderCurrency = selectedOrder ? selectedOrder.currency : undefined;
    for (let index = 0; index < items.length; index += 1) {
      const item = items[index];
      const itemId = `item-${index}`;
      this.state.selectedVariants[itemId] = null;

      try {
        const product = await this._fetchProductJson(item.product.handle);
        const owv = product.options_with_values || [];
        const sizeIndex = owv.findIndex((o) => o.name.toLowerCase().includes('size'));
        if (sizeIndex === -1) throw new Error('no size option');

        const sizes = (owv[sizeIndex].values || []).filter((v) => v.toLowerCase() !== 'awaiting size');
        if (sizes.length === 0) throw new Error('no sizes');

        // Default to the first size and resolve its variant id.
        const firstVariant = this._findVariantId(product, item.originalNonSizeOptions, sizes[0], sizeIndex);
        this.state.selectedVariants[itemId] = firstVariant;

        const sizeOptions = [`<option value="">${this.config.sizePlaceholder}</option>`]
          .concat(
            sizes.map((size, i) => `<option value="${this._esc(size)}"${i === 0 ? ' selected' : ''}>${this._esc(size)}</option>`)
          )
          .join('');

        const details = Object.entries(item.originalNonSizeOptions || {})
          .map(
            ([k, v]) =>
              `<div class="ring-size-verify__item-row"><span class="ring-size-verify__item-label">${this._esc(k)}:</span><span>${this._esc(v)}</span></div>`
          )
          .join('');

        blocks.push(`
          <div class="ring-size-verify__item">
            <p class="ring-size-verify__item-title">${this._esc(item.product.title)}</p>
            <div class="ring-size-verify__item-details">
              ${details}
              <div class="ring-size-verify__item-row">
                <span class="ring-size-verify__item-label">Price:</span>
                <span>${this._esc(this._formatPrice(item.originalVariant.price, orderCurrency))}</span>
              </div>
            </div>
            <div class="ring-size-verify__item-size">
              <label class="ring-size-verify__label" for="rsv-size-${itemId}">Select size</label>
              <select class="ring-size-verify__select" id="rsv-size-${itemId}" data-rsv-size data-item-id="${itemId}" data-size-index="${sizeIndex}" data-handle="${this._esc(item.product.handle)}">
                ${sizeOptions}
              </select>
            </div>
          </div>
        `);
      } catch (err) {
        blocks.push(`
          <div class="ring-size-verify__item">
            <p class="ring-size-verify__item-title">${this._esc(item.product.title)}</p>
            <p class="ring-size-verify__item-error">Could not load sizes for this product.</p>
          </div>
        `);
      }
    }

    this.refs.items.innerHTML = blocks.join('');
    // Cache product JSON per handle so a size change does not refetch.
    this._updateSubmitState();
  }

  async _onSizeChange(select) {
    const itemId = select.dataset.itemId;
    const sizeIndex = parseInt(select.dataset.sizeIndex, 10);
    const handle = select.dataset.handle;
    const selectedSize = select.value;
    const index = parseInt(itemId.replace('item-', ''), 10);
    const item = this.state.items[index];

    if (!selectedSize || !item) {
      this.state.selectedVariants[itemId] = null;
    } else {
      const product = await this._fetchProductJson(handle);
      this.state.selectedVariants[itemId] = this._findVariantId(
        product,
        item.originalNonSizeOptions,
        selectedSize,
        sizeIndex
      );
    }
    this._updateSubmitState();
  }

  _updateSubmitState() {
    const ids = Object.keys(this.state.selectedVariants);
    const allChosen = ids.length > 0 && ids.every((id) => this.state.selectedVariants[id]);
    if (this.refs.submitBtn) this.refs.submitBtn.disabled = !allChosen;
  }

  _findVariantId(product, originalNonSizeOptions, selectedSize, sizeIndex) {
    const owv = product.options_with_values || [];
    const match = (product.variants || []).find((variant) => {
      const opts = variant.options || [];
      if (opts[sizeIndex] !== selectedSize) return false;
      return owv.every((opt, i) => (i === sizeIndex ? true : originalNonSizeOptions[opt.name] === opts[i]));
    });
    return match ? match.id : null;
  }

  // ---------- step 5: submit ----------

  async _onSubmit(button) {
    const variantIds = Object.values(this.state.selectedVariants).filter(Boolean);
    if (variantIds.length === 0) {
      return this._message(this._err('generic'), 'error');
    }

    // Parity with the original flow: one order is created for the first variant.
    const variantId = variantIds[0];

    this._setBusy(button, true);
    const labelEl = button.querySelector('.ring-size-verify__btn-label');
    const restoreLabel = labelEl ? labelEl.textContent : '';
    if (labelEl && this.config.submitting) labelEl.textContent = this.config.submitting;

    const { ok, data, network } = await this._api('/submit', {
      method: 'POST',
      body: { orderId: this.state.currentOrder, variantId: String(variantId) },
    });

    this._setBusy(button, false);
    if (labelEl) labelEl.textContent = restoreLabel;

    if (network) return this._message(this._err('network'), 'error');

    if (ok && data && data.success) {
      if (this.refs.newOrder) this.refs.newOrder.textContent = data.data.newOrder || '';
      this._show('success');
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (data && data.error && data.error.toLowerCase().includes('already')) {
      return this._message(this._err('generic'), 'error');
    }
    this._message(this._err('generic'), 'error');
  }

  // ---------- utilities ----------

  async _fetchProductJson(handle) {
    if (!this._productCache) this._productCache = {};
    if (this._productCache[handle]) return this._productCache[handle];
    const res = await fetch(`/products/${handle}?view=product-json`);
    if (!res.ok) throw new Error(`product-json ${res.status}`);
    const data = await res.json();
    this._productCache[handle] = data;
    return data;
  }

  _formatPrice(amount, currencyCode) {
    if (amount === null || amount === undefined || amount === '') return '';
    const number = typeof amount === 'number' ? amount : parseFloat(amount);
    if (!Number.isFinite(number)) return '';
    const currency =
      currencyCode || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
    if (currency) {
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(number);
      } catch (err) {
        // Unknown currency code — fall back to the bare amount below.
      }
    }
    return String(amount);
  }

  _esc(value) {
    return String(value === null || value === undefined ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('ring-size-verify')) {
  customElements.define('ring-size-verify', RingSizeVerify);
}
